import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'

// MUI Imports
import {
  createFilterOptions,
  Grid,
  Typography,
  Paper,
  Autocomplete,
  Chip,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Box
} from '@mui/material'

import { useGlobalStoreData } from '../../../../../../src/store/globalStore'
import AiTemplateArrayInput from './AiTemplateArrayInput'

const filterOptions = createFilterOptions({
  ignoreCase: true,
  limit: 20
})

const CampaignInformation = ({ control, errors, countries, alwaysShowPlaceholder = true }) => {
  const { t } = useTranslation()

  const hsCodesGSD = useGlobalStoreData.hsCodes()

  const shrinkProps = alwaysShowPlaceholder ? { InputLabelProps: { shrink: true } } : {}

  const hsCodeOptions = useMemo(() => hsCodesGSD || [], [hsCodesGSD])
  const countryOptions = useMemo(() => countries || [], [countries])

  const handleHsCodesChange = useCallback((onChange) => (e, data) => {
    onChange(data)
  }, [])

  const handleCountryChange = useCallback((onChange) => (e, data) => {
    onChange(data)
  }, [])

  const renderHsCodeTags = useCallback((value, getTagProps) =>
    value.map((option, index) => (
      <Chip
        variant="outlined"
        label={option.value}
        {...getTagProps({ index })}
        key={option.value}
      />
    )), [])

  const hsCodeGetOptionLabel = useCallback((opt) => `${opt.value}-${opt.label}`, [])
  const hsCodeIsOptionEqual = useCallback((option, value) => option.value === value.value, [])

  const countryGetOptionLabel = useCallback((opt) => opt?.label || '', [])
  const countryIsOptionEqual = useCallback((option, value) => option?.countryCode === value?.countryCode, [])

  return (
    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
        {t('mailTemplate.campaigns.modal.campaignInformation') || 'Campaign Information'}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                {...shrinkProps}
                fullWidth
                size="small"
                required
                label={t('mailTemplate.campaigns.modal.name')}
                placeholder={t('mailTemplate.campaigns.modal.nameP')}
                error={!!errors.name}
                helperText={errors.name && t('mailTemplate.campaigns.modal.errors.name')}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 40
                  }
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="hsCodes"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                multiple
                size="small"
                value={value || []}
                onChange={handleHsCodesChange(onChange)}
                options={hsCodeOptions}
                getOptionLabel={hsCodeGetOptionLabel}
                isOptionEqualToValue={hsCodeIsOptionEqual}
                filterOptions={filterOptions}
                renderTags={renderHsCodeTags}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...shrinkProps}
                    size="small"
                    required
                    label={t('mailTemplate.campaigns.modal.hsCodes')}
                    placeholder={t('mailTemplate.campaigns.modal.hsCodesP')}
                    error={!!errors.hsCodes}
                    helperText={errors.hsCodes && t('mailTemplate.campaigns.modal.errors.hsCodes')}
                    sx={{
                      '& .MuiInputBase-root': {
                        minHeight: 40,
                        height: 'auto',
                        padding: '8px 14px',
                        '&.MuiInputBase-sizeSmall': {
                          padding: '8px 14px'
                        }
                      },
                      '& .MuiAutocomplete-inputRoot': {
                        padding: '0 !important'
                      },
                      '& .MuiAutocomplete-input': {
                        padding: '0 6px 0 12px !important',
                        minHeight: '24px'
                      },
                      '& .MuiChip-root': {
                        height: '24px',
                        fontSize: '12px'
                      }
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="importerCountryCode"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                value={value || null}
                size="small"
                onChange={handleCountryChange(onChange)}
                options={countryOptions}
                getOptionLabel={countryGetOptionLabel}
                isOptionEqualToValue={countryIsOptionEqual}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...shrinkProps}
                    size="small"
                    required
                    label={t('mailTemplate.campaigns.modal.importerCountryCode')}
                    placeholder={t('mailTemplate.campaigns.modal.importerCountryCodeP')}
                    error={!!errors.importerCountryCode}
                    helperText={errors.importerCountryCode && t('mailTemplate.campaigns.modal.errors.importerCountryCode')}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 40
                      }
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="currentCountryCode"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                value={value || null}
                size="small"
                onChange={handleCountryChange(onChange)}
                options={countryOptions}
                getOptionLabel={countryGetOptionLabel}
                isOptionEqualToValue={countryIsOptionEqual}
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...shrinkProps}
                    size="small"
                    required
                    label={t('mailTemplate.campaigns.modal.currentCountryCode')}
                    placeholder={t('mailTemplate.campaigns.modal.currentCountryCodeP')}
                    error={!!errors.currentCountryCode}
                    helperText={errors.currentCountryCode && t('mailTemplate.campaigns.modal.errors.currentCountryCode')}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 40
                      }
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {t('mailTemplate.campaigns.modal.matchKeywords')}
              </Typography>
              <Controller
                name="productListOp"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ToggleButtonGroup
                    value={value || 'or'}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        onChange(newValue)
                      }
                    }}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1.5,
                        py: 0.25,
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        borderColor: 'divider',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.dark'
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value="or">
                      {t('mailTemplate.campaigns.modal.matchAny')}
                    </ToggleButton>
                    <ToggleButton value="and">
                      {t('mailTemplate.campaigns.modal.matchAll')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Box>
            <Controller
              name="productName"
              control={control}
              render={({ field: { value = [], onChange } }) => (
                <AiTemplateArrayInput
                  label={t('mailTemplate.campaigns.modal.productName')}
                  placeholder={t('mailTemplate.campaigns.modal.productNameP')}
                  alwaysShowPlaceholder={alwaysShowPlaceholder}
                  items={value}
                  required={true}
                  onAdd={(newValue) => {
                    if (newValue.trim()) {
                      onChange([...value, { value: newValue.trim() }])
                    }
                  }}
                  onRemove={(index) => {
                    const newValue = [...value]
                    newValue.splice(index, 1)
                    onChange(newValue)
                  }}
                />
              )}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default CampaignInformation
